import { useCallback, useContext, useMemo, useSyncExternalStore } from 'react';
import { QueryClientContext, useQuery, type QueryClient } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import type { ScopeEntry } from '@nexa/auth';
import { getMe, ME_QUERY_KEY, type OperatorMe } from '@/lib/me/api';
import { AccessOverrideContext } from './access-context';
import { resolveAccess, type IAccessSnapshot } from './resolve';

// The `GET /v1/me` readers behind every access hook — one cache entry, two kinds of reader:
//
//   - ACTIVE (`useActiveAccessSnapshot`): a `useQuery` observer that fetches when signed
//     in. Used by the access boundary, the route guards and `<Can>`.
//   - PASSIVE (`useCachedAccessSnapshot`): reads what the cache already holds and
//     re-renders when it changes — no request, and no QueryClient REQUIRED. That is what
//     lets `useSession()`, `<RoleGate>` (≈50 call sites, many rendered in specs with no
//     BFF and no QueryClientProvider), the chrome and the airport-scope hook follow the
//     BFF's answer without each becoming a network caller.
//
// Live by push, never by poll: the entry is invalidated by `WS access.changed`, by a socket
// reconnect (`lib/ws/socket.ts`) and by a 403 on any other call (`use-access-live.ts`).

/** Same freshness as `useOperator()` — they share the cache entry. */
const STALE_MS = 5 * 60_000;

export interface IAccessSnapshotState {
  /** The effective access, or `null` while nothing authoritative is known. */
  snapshot: IAccessSnapshot | null;
  /** Unknown yet, and a read that would resolve it is pending / in flight. */
  loading: boolean;
  /** A read of `/v1/me` is in flight (first load or a refresh). */
  refreshing: boolean;
  /** The last read's error, if it failed. */
  error: unknown;
}

function compose(
  fixed: boolean,
  dev: IAccessSnapshot | null,
  me: OperatorMe | undefined,
  isError: boolean,
  claimScope: readonly ScopeEntry[] | null,
): IAccessSnapshot | null {
  if (fixed) return dev;
  const resolved = resolveAccess({ me, claimScope, dev });
  // Dev provider with the BFF unreachable: the developer's VITE_DEV_ROLE still answers.
  if (!resolved && dev && isError) return dev;
  return resolved;
}

/** Fetching reader. Requires a QueryClientProvider (it always exists inside the app). */
export function useActiveAccessSnapshot(
  claimScope: readonly ScopeEntry[] | null,
): IAccessSnapshotState {
  const { isAuthenticated } = useAuth0();
  const override = useContext(AccessOverrideContext);
  const fixed = override !== null && !override.preferStore;
  const enabled = isAuthenticated && !fixed;
  const dev = override?.access ?? null;

  const query = useQuery<OperatorMe>({
    queryKey: ME_QUERY_KEY,
    queryFn: getMe,
    staleTime: STALE_MS,
    refetchOnWindowFocus: false,
    enabled,
  });

  const snapshot = useMemo(
    () => compose(fixed, dev, query.data, query.isError, claimScope),
    [fixed, dev, query.data, query.isError, claimScope],
  );
  return {
    snapshot,
    loading: snapshot === null && enabled && (query.isPending || query.isFetching),
    refreshing: query.isFetching,
    error: query.isError ? query.error : null,
  };
}

const noopSubscribe = (): (() => void) => () => undefined;

/** Subscribe to one value of the `/v1/me` cache entry; `undefined` without a client. */
function useMeCacheValue<T>(
  client: QueryClient | undefined,
  read: (c: QueryClient) => T,
): T | undefined {
  const subscribe = useCallback(
    (onChange: () => void) =>
      client ? client.getQueryCache().subscribe(onChange) : noopSubscribe(),
    [client],
  );
  const getSnapshot = useCallback(() => (client ? read(client) : undefined), [client, read]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

const readData = (c: QueryClient) => c.getQueryData<OperatorMe>(ME_QUERY_KEY);
const readFetching = (c: QueryClient) => c.getQueryState(ME_QUERY_KEY)?.fetchStatus === 'fetching';
const readError = (c: QueryClient) => c.getQueryState(ME_QUERY_KEY)?.error ?? null;

/** Non-fetching reader — tolerant of a missing QueryClientProvider. */
export function useCachedAccessSnapshot(
  claimScope: readonly ScopeEntry[] | null,
): IAccessSnapshotState {
  const client = useContext(QueryClientContext);
  const override = useContext(AccessOverrideContext);
  const fixed = override !== null && !override.preferStore;
  const dev = override?.access ?? null;

  const me = useMeCacheValue(client, readData);
  const fetching = useMeCacheValue(client, readFetching) ?? false;
  const error = useMeCacheValue(client, readError) ?? null;

  const snapshot = useMemo(
    () => compose(fixed, dev, me, error !== null, claimScope),
    [fixed, dev, me, error, claimScope],
  );
  return { snapshot, loading: snapshot === null && fetching, refreshing: fetching, error };
}
